import { Messages } from '~src/codegen/Localize';

import { RoutingFilterErrorCode } from '../types/RoutingFilterErrorCode';

// Specific message for a known error code, or undefined if the code is
// unrecognized — callers pick the fallback appropriate to their context.
function getSpecificRoutingFilterErrorMessage(errorCode: string | undefined): string | undefined {
  switch (errorCode) {
    case RoutingFilterErrorCode.DOMAIN_CONFLICT:
    case RoutingFilterErrorCode.PROJECT_CONFLICT:
    case RoutingFilterErrorCode.DOMAIN_ONLY_ROUTING_RULE_ALREADY_EXISTS:
      return Messages.bridgeSiteSettingsRoutingFilterConflictDescription();
    case RoutingFilterErrorCode.DOMAIN_ALREADY_BLOCKED:
    case RoutingFilterErrorCode.DOMAIN_TO_BE_BLOCKED_HAS_EXISTING_RULES:
      return Messages.bridgeSiteSettingsRoutingFilterErrorDomainPermissionConflict();
    case RoutingFilterErrorCode.NOT_FOUND:
      return Messages.bridgeSiteSettingsRoutingFilterErrorNotFound();
    case RoutingFilterErrorCode.INVALID_POOL_ID:
      return Messages.bridgeSiteSettingsRoutingFilterErrorInvalidPoolId();
    case RoutingFilterErrorCode.INVALID_DOMAIN:
      return Messages.bridgeSiteSettingsRoutingFilterErrorInvalidDomain();
    case RoutingFilterErrorCode.PROJECTS_NOT_FOUND:
      return Messages.bridgeSiteSettingsRoutingFilterErrorProjectsNotFound();
    case RoutingFilterErrorCode.PERMISSION_DENIED:
      return Messages.bridgeSiteSettingsRoutingFilterErrorPermissionDenied();
    default:
      return undefined;
  }
}

export function getRoutingFilterErrorMessage(errorCode: string | undefined): string {
  return getSpecificRoutingFilterErrorMessage(errorCode) ?? Messages.bridgeSiteSettingsEditRoutingFilterError();
}

// Message for a single conflict error shown in the conflict popover. Falls back
// to the generic disambiguation guidance for conflict codes the client hasn't
// mapped, rather than the "Failed to save" default which reads oddly in a list.
export function getRoutingFilterConflictMessage(errorCode: string | undefined): string {
  return getSpecificRoutingFilterErrorMessage(errorCode) ?? Messages.bridgeSiteSettingsRoutingFilterConflictDescription();
}

export type RoutingFilterConflictMessage = Readonly<{ errorCode: string, message: string }>;

// Distinct conflict messages, deduped on the stable error code (not the resolved
// text) so codes that share a fallback message still render separately and a code
// repeated across several filters collapses to one. Shared by the table row's
// conflict popover and the dialog's conflict banner so the two read identically.
export function getDistinctConflictMessages(
  conflictErrors: ReadonlyArray<{ errorCode: string }> | undefined
): RoutingFilterConflictMessage[] {
  const messages: RoutingFilterConflictMessage[] = [];
  const seen = new Set<string>();
  for (const err of conflictErrors ?? []) {
    if (!seen.has(err.errorCode)) {
      seen.add(err.errorCode);
      messages.push({ errorCode: err.errorCode, message: getRoutingFilterConflictMessage(err.errorCode) });
    }
  }
  return messages;
}

// The `invalidFields` key under which the server lists the LUIDs of the projects
// that caused a conflict. Named so the string isn't duplicated across the readers
// below and a server-side rename has a single point to update.
const CONFLICTING_PROJECTS_FIELD = 'projects';

// The subset of a conflict error the project-id readers below depend on: the
// server's per-field invalid values. Kept structural (rather than importing
// ConflictError) so both the mapped ConflictError and the raw server error shape
// satisfy it, but explicit about the one field we actually read.
type ConflictErrorInvalidFields = Readonly<{
  invalidFields?: Record<string, ReadonlyArray<string>>;
}>;

// Union of the project ids the server flagged as conflicting across all conflict
// errors (invalidFields.projects). Shared by the table row's project highlight
// and the reopened dialog so both highlight exactly the same projects.
export function collectConflictingProjectIds(
  conflictErrors: ReadonlyArray<ConflictErrorInvalidFields> | undefined
): Set<string> {
  const ids = new Set<string>();
  for (const err of conflictErrors ?? []) {
    for (const pid of err.invalidFields?.[CONFLICTING_PROJECTS_FIELD] ?? []) {
      ids.add(pid);
    }
  }
  return ids;
}

// True when at least one project the server flagged as conflicting is still in
// the current selection. A deselected project can't contribute to a conflict, so
// both the dialog banner and the selector's conflict-tab focus gate on this.
// Extracted so the project selector and the dialog body share one definition
// rather than each carrying its own copy of the intersection check.
export function hasSelectedConflictingProject(
  conflictingProjectIds: ReadonlySet<string> | undefined,
  selectedProjectIds: ReadonlySet<string>
): boolean {
  return Array.from(conflictingProjectIds ?? []).some(id => selectedProjectIds.has(id));
}
